import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { IntakeEnvelope, IntakePlan, IntakeScope } from "./types";

const base = "/me/intake/master-product";
const planKey = ["me", "intake", "master-product"] as const;

export function useMasterProductPlan(scope: IntakeScope) {
  const { session } = useAuth();
  return useQuery({
    queryKey: [...planKey, session?.user.id, scope],
    queryFn: () =>
      apiFetch<IntakePlan>(`${base}/plan`, {
        method: "POST",
        body: JSON.stringify(scope),
      }),
    staleTime: 0,
    gcTime: 0,
    enabled: Boolean(session?.user.id),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.mode === "legacy" ? 4000 : false,
  });
}

export function useMasterProductDraft() {
  return useMutation({
    mutationFn: (body: {
      session_id: string;
      revision: number;
      answers: IntakeEnvelope;
    }) =>
      apiFetch<IntakePlan>(`${base}/draft`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    retry: false,
  });
}

export function useMasterProductSubmit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      session_id: string;
      revision: number;
      answers: IntakeEnvelope;
      idempotency_key: string;
      master_confirmation?: { submission_id: string; unchanged: true };
    }) =>
      apiFetch<IntakePlan>(`${base}/submit`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    retry: false,
    onSuccess: () => {
      for (const queryKey of [
        ["me", "required-intakes"],
        ["me", "renewal-checkins"],
        ["me", "onboarding"],
        ["bootstrap"],
      ])
        void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useMasterProductSession(sessionId: string, enabled: boolean) {
  const { session } = useAuth();
  return useQuery({
    queryKey: [...planKey, session?.user.id, "session", sessionId],
    queryFn: () =>
      apiFetch<IntakePlan>(`${base}/session/${encodeURIComponent(sessionId)}`),
    enabled: enabled && Boolean(session?.user.id),
    gcTime: 0,
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.clinical_ready ? false : 5000,
  });
}
